import { useState, useRef, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Loader2, CheckCircle2, AlertCircle, ChevronDown, ChevronUp,
  TrendingUp, AlertTriangle, Send, RotateCcw,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { coworkApi, API_BASE_URL, authHeaders, type GoogleAdsAgentEvent } from '../api/coworkApi';

// ─── Types ────────────────────────────────────────────────────────────────────

const TOOL_META: Record<string, { icon: string; label: string }> = {
  get_account_overview:       { icon: '📊', label: 'Account Overview' },
  get_campaign_performance:   { icon: '🚀', label: 'Campaign Performance' },
  get_keyword_performance:    { icon: '🔑', label: 'Keyword Analysis' },
  get_search_terms_report:    { icon: '🔍', label: 'Search Terms' },
  get_ad_performance:         { icon: '📝', label: 'Ad Performance' },
  get_device_performance:     { icon: '📱', label: 'Device Breakdown' },
  get_geographic_performance: { icon: '🌍', label: 'Geographic Data' },
  get_budget_utilization:     { icon: '💰', label: 'Budget Analysis' },
  pause_campaign:             { icon: '⏸️', label: 'Pause Campaign' },
  update_campaign_budget:     { icon: '💸', label: 'Update Budget' },
  add_negative_keywords:      { icon: '🚫', label: 'Add Negatives' },
  create_campaign:            { icon: '🏗️', label: 'Create Campaign' },
  enable_campaign:            { icon: '▶️', label: 'Enable Campaign' },
  create_report:              { icon: '📋', label: 'Generate Report' },
};

type TraceItem =
  | { kind: 'step'; message: string }
  | { kind: 'tool_call'; name: string; input: Record<string, unknown>; id: string; result?: string; status: 'running' | 'done' | 'error' }
  | { kind: 'write_proposed'; action: string; details: string; toolName: string; toolInput: Record<string, unknown> };

type ChatMessage =
  | { role: 'user'; content: string }
  | { role: 'agent'; content: string; trace: TraceItem[]; status: 'streaming' | 'done' | 'error'; error?: string; reportCreated?: { artifactId: string; title: string } };

interface SavedSession {
  id: string;
  title: string;
  createdAt: string;
  messages: ChatMessage[];
  history: unknown[];
}

const STORAGE_KEY = 'google-ads-sessions';

function loadSessions(): SavedSession[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveSessions(sessions: SavedSession[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions.slice(0, 30)));
  } catch {
    /* storage full or unavailable — sessions are a convenience, not state of record */
  }
}

function sessionTitle(messages: ChatMessage[]): string {
  const first = messages.find(m => m.role === 'user');
  if (!first) return 'New conversation';
  return first.content.slice(0, 60) + (first.content.length > 60 ? '…' : '');
}


const SUGGESTED = [
  'Full account audit: find wasted spend and top 10 quick wins',
  'Which campaigns should I pause or cut budget on?',
  'Find search terms wasting money: suggest negatives',
  'How is my budget being spent? Any pacing issues?',
  'Analyze keyword quality scores and recommend fixes',
  'How is performance split across mobile vs desktop?',
];

// ─── Markdown ─────────────────────────────────────────────────────────────────

const MD = {
  h1: ({ children }: { children?: React.ReactNode }) => <h1 className="text-[17px] text-[#101828] font-bold mt-5 mb-2">{children}</h1>,
  h2: ({ children }: { children?: React.ReactNode }) => <h2 className="text-[14px] text-[#101828] font-bold mt-4 mb-1.5 border-b border-gray-100 pb-1">{children}</h2>,
  h3: ({ children }: { children?: React.ReactNode }) => <h3 className="text-[13px] text-[#101828] font-semibold mt-3 mb-1">{children}</h3>,
  p: ({ children }: { children?: React.ReactNode }) => <p className="text-[13px] text-[#344054] leading-relaxed mb-2.5">{children}</p>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc list-inside space-y-1 mb-2.5 text-[13px] text-[#344054]">{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal list-inside space-y-1 mb-2.5 text-[13px] text-[#344054]">{children}</ol>,
  li: ({ children }: { children?: React.ReactNode }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold text-[#101828]">{children}</strong>,
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) =>
    className
      ? <pre className="bg-[#f9fafb] border border-gray-200 rounded-xl p-3.5 overflow-x-auto mb-2.5 text-[11.5px] font-mono text-[#344054]"><code>{children}</code></pre>
      : <code className="bg-[#f3f4f6] px-1.5 py-0.5 rounded text-[11.5px] font-mono text-[#4285f4]">{children}</code>,
  table: ({ children }: { children?: React.ReactNode }) => <div className="overflow-x-auto mb-3"><table className="w-full text-[12px] border-collapse">{children}</table></div>,
  thead: ({ children }: { children?: React.ReactNode }) => <thead className="bg-[#f9fafb]">{children}</thead>,
  th: ({ children }: { children?: React.ReactNode }) => <th className="text-left px-3 py-2 text-[11px] text-[#667085] font-semibold border border-gray-200 uppercase tracking-wide">{children}</th>,
  td: ({ children }: { children?: React.ReactNode }) => <td className="px-3 py-2 border border-gray-200 text-[#344054]">{children}</td>,
  blockquote: ({ children }: { children?: React.ReactNode }) => <blockquote className="border-l-4 border-[#4285f4]/30 pl-4 italic text-[#667085] my-2.5">{children}</blockquote>,
};

// ─── Trace row ────────────────────────────────────────────────────────────────

function TraceRow({ item }: { item: TraceItem }) {
  const [expanded, setExpanded] = useState(false);
  if (item.kind === 'step') {
    return (
      <div className="flex items-center gap-2 text-[11px] text-[#98a2b3] py-0.5">
        <span className="w-1 h-1 rounded-full bg-[#d0d5dd] shrink-0" />
        {item.message}
      </div>
    );
  }
  if (item.kind === 'write_proposed') {
    const ba = getBeforeAfter(item.toolName, item.toolInput);
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
        <span className="text-[12px]">{ba.icon}</span>
        <span className="text-[11px] text-amber-800 font-medium">{ba.label}</span>
        <span className="text-[10px] text-amber-600 ml-auto">proposal queued ↓</span>
      </div>
    );
  }
  const meta = TOOL_META[item.name] ?? { icon: '🔧', label: item.name };
  const isWrite = ['pause_campaign', 'update_campaign_budget', 'add_negative_keywords', 'create_campaign', 'enable_campaign'].includes(item.name);
  return (
    <div className={`border rounded-lg overflow-hidden ${isWrite ? 'border-orange-200 bg-orange-50/30' : 'border-gray-100 bg-white'}`}>
      <div className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50/80" onClick={() => item.result && setExpanded(v => !v)}>
        <span className="text-[12px] shrink-0">{meta.icon}</span>
        <span className="text-[11px] text-[#667085] font-medium flex-1">{meta.label}</span>
        {isWrite && <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded-full">write</span>}
        {item.status === 'running' && <Loader2 className="w-3 h-3 animate-spin text-[#4285f4]" />}
        {item.status === 'done' && <CheckCircle2 className="w-3 h-3 text-[#10b981]" />}
        {item.status === 'error' && <AlertCircle className="w-3 h-3 text-red-400" />}
        {item.result && (expanded ? <ChevronUp className="w-3 h-3 text-[#98a2b3]" /> : <ChevronDown className="w-3 h-3 text-[#98a2b3]" />)}
      </div>
      {expanded && item.result && (
        <div className="px-3 pb-2.5 border-t border-gray-100">
          <pre className="mt-2 text-[10px] text-[#667085] font-mono whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed">{item.result}</pre>
        </div>
      )}
    </div>
  );
}

// ─── Before/After helper ──────────────────────────────────────────────────────

function getBeforeAfter(toolName: string, input: Record<string, unknown>): {
  icon: string; label: string; before: string; after: string; entity?: string;
} {
  const campaignId = input.campaign_id as string | undefined;
  const campaignName = input.campaign_name as string | undefined;
  const entity = campaignName ?? (campaignId ? `Campaign ${campaignId}` : undefined);

  switch (toolName) {
    case 'enable_campaign':
      return { icon: '▶️', label: 'Enable Campaign', before: 'PAUSED', after: 'ENABLED & SPENDING', entity };
    case 'pause_campaign':
      return { icon: '⏸️', label: 'Pause Campaign', before: 'ENABLED', after: 'PAUSED', entity };
    case 'update_campaign_budget': {
      const budget = input.new_daily_budget_usd as number | undefined;
      return { icon: '💰', label: 'Update Daily Budget', before: 'Current budget', after: budget != null ? `$${budget}/day` : 'New budget', entity };
    }
    case 'add_negative_keywords': {
      const kws = input.keywords as string[] | undefined;
      const count = kws?.length ?? 0;
      const preview = kws?.slice(0, 3).join(', ') ?? '';
      return { icon: '🚫', label: 'Block Irrelevant Keywords', before: 'Wasting budget on bad searches', after: `${count} term${count !== 1 ? 's' : ''} blocked: ${preview}${count > 3 ? '…' : ''}`, entity };
    }
    case 'create_campaign':
      return { icon: '🏗️', label: 'Create New Campaign', before: 'No campaign', after: `"${input.campaign_name}" created (PAUSED, ready to review)`, entity: undefined };
    default:
      return { icon: '🔧', label: toolName.replace(/_/g, ' '), before: 'Current state', after: 'Updated', entity };
  }
}

// ─── Agent bubble ─────────────────────────────────────────────────────────────

function AgentBubble({
  msg,
  onExecuteAction,
}: {
  msg: Extract<ChatMessage, { role: 'agent' }>;
  onExecuteAction?: (action: string) => void;
}) {
  const [showTrace, setShowTrace] = useState(false);
  const toolCount = msg.trace.filter(t => t.kind === 'tool_call').length;
  const doneCount = msg.trace.filter(t => t.kind === 'tool_call' && (t as Extract<TraceItem, { kind: 'tool_call' }>).status === 'done').length;
  const writeProposals = msg.trace.filter((t): t is Extract<TraceItem, { kind: 'write_proposed' }> => t.kind === 'write_proposed');

  return (
    <div className="flex gap-3 items-start">
      <div className="w-7 h-7 rounded-lg shrink-0 flex items-center justify-center mt-0.5"
        style={{ background: 'linear-gradient(135deg, #4285f4, #34a853)' }}>
        <TrendingUp className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        {msg.trace.length > 0 && (
          <div className="bg-[#fafafa] border border-gray-100 rounded-xl overflow-hidden">
            <button className="w-full flex items-center justify-between px-3.5 py-2.5 hover:bg-gray-50 transition-colors" onClick={() => setShowTrace(v => !v)}>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[#667085] font-semibold">
                  {msg.status === 'streaming'
                    ? `Analyzing… ${toolCount > 0 ? `(${doneCount}/${toolCount} queries)` : ''}`
                    : `${toolCount} API call${toolCount !== 1 ? 's' : ''}`}
                </span>
                {msg.status === 'streaming' && <Loader2 className="w-3 h-3 text-[#4285f4] animate-spin" />}
                {msg.status === 'done' && toolCount > 0 && <CheckCircle2 className="w-3 h-3 text-[#10b981]" />}
              </div>
              {showTrace ? <ChevronUp className="w-3 h-3 text-[#98a2b3]" /> : <ChevronDown className="w-3 h-3 text-[#98a2b3]" />}
            </button>
            {showTrace && (
              <div className="px-3.5 pb-3 space-y-1.5 border-t border-gray-50">
                {msg.trace.map((item, i) => <TraceRow key={i} item={item} />)}
              </div>
            )}
          </div>
        )}
        {msg.status === 'error' && msg.error && (
          <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <p className="text-[13px] text-red-600">{msg.error}</p>
          </div>
        )}
        {msg.content && (
          <div className="bg-white border border-gray-100 rounded-xl px-5 py-4 prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{msg.content}</ReactMarkdown>
            {msg.status === 'streaming' && (
              <span className="inline-block w-1.5 h-4 bg-[#4285f4] animate-pulse rounded-sm ml-0.5 align-middle" />
            )}
          </div>
        )}
        {/* Action CTA cards for proposed write operations */}
        {msg.status === 'done' && writeProposals.length > 0 && onExecuteAction && (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-[#667085] uppercase tracking-wide px-1">
              ⚡ {writeProposals.length} action{writeProposals.length !== 1 ? 's' : ''} ready to apply
            </p>
            {writeProposals.map((proposal, i) => {
              const ba = getBeforeAfter(proposal.toolName, proposal.toolInput);
              return (
                <div key={i} className="border border-[#e5e7eb] bg-white rounded-xl overflow-hidden shadow-sm">
                  {/* Header */}
                  <div className="flex items-center gap-2.5 px-4 py-3 border-b border-[#f3f4f6]" style={{ background: 'linear-gradient(to right, #f0f9ff, #fafafa)' }}>
                    <span className="text-base">{ba.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-[#101828] leading-tight">{ba.label}</p>
                      {ba.entity && <p className="text-[11px] text-[#667085] truncate">{ba.entity}</p>}
                    </div>
                    <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium shrink-0">Pending approval</span>
                  </div>
                  {/* Before → After */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div className="flex-1 text-center px-3 py-2 rounded-lg bg-[#fef2f2] border border-[#fecaca]">
                      <p className="text-[10px] text-[#9ca3af] font-medium uppercase tracking-wide mb-0.5">Before</p>
                      <p className="text-[13px] font-semibold text-[#dc2626]">{ba.before}</p>
                    </div>
                    <div className="text-[18px] text-[#9ca3af] shrink-0">→</div>
                    <div className="flex-1 text-center px-3 py-2 rounded-lg bg-[#f0fdf4] border border-[#bbf7d0]">
                      <p className="text-[10px] text-[#9ca3af] font-medium uppercase tracking-wide mb-0.5">After</p>
                      <p className="text-[13px] font-semibold text-[#16a34a]">{ba.after}</p>
                    </div>
                  </div>
                  {/* Apply actions */}
                  <div className="flex gap-2 px-4 pb-3.5">
                    <button
                      className="flex-1 text-[12px] py-2 border border-[#d1d5db] text-[#374151] rounded-lg hover:bg-[#f9fafb] transition-colors font-medium"
                      onClick={() => onExecuteAction(`What exactly will happen if I apply: ${proposal.action}? Give me the specific before and after for my account.`)}
                    >
                      Explain in detail
                    </button>
                    <button
                      className="flex-1 text-[12px] py-2 rounded-lg text-white font-semibold transition-all hover:opacity-90 shadow-sm"
                      style={{ background: 'linear-gradient(135deg, #4285f4, #34a853)' }}
                      onClick={() => onExecuteAction(`Yes, apply it: ${proposal.action}. Set confirm=true and execute now.`)}
                    >
                      ✓ Apply Now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Report saved toast */}
        {msg.reportCreated && (
          <div className="flex items-center gap-2.5 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
            <span className="text-base">📊</span>
            <div className="flex-1">
              <p className="text-[12px] font-semibold text-blue-800">Report saved to REPORTS</p>
              <p className="text-[11px] text-blue-600">{msg.reportCreated.title}</p>
            </div>
          </div>
        )}
        {msg.status === 'streaming' && !msg.content && msg.trace.length === 0 && (
          <div className="flex items-center gap-2 text-[13px] text-[#98a2b3]">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Connecting to Google Ads…
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Setup guide ──────────────────────────────────────────────────────────────

function SetupGuide({ missing }: { missing: string[] }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="w-7 h-7 rounded-lg shrink-0 flex items-center justify-center mt-0.5"
        style={{ background: 'linear-gradient(135deg, #4285f4, #34a853)' }}>
        <TrendingUp className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="flex-1 bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
        <p className="text-[13px] text-[#101828] font-semibold">Google Ads API setup required</p>
        <p className="text-[12px] text-[#667085]">Add these to your <code className="bg-white px-1.5 py-0.5 rounded border border-gray-200 text-[11px] font-mono">.env</code> file and restart the backend:</p>
        <div className="bg-white border border-gray-200 rounded-lg p-3 font-mono text-[11px] space-y-1 text-[#344054]">
          {missing.map(k => <p key={k}><span className="text-red-400">{k}</span>=your_value_here</p>)}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function GoogleAdsAgentPage({
  onBack,
  initialSessionId,
  onSessionSaved,
  onReportCreated,
}: {
  onBack: () => void;
  initialSessionId?: string;
  onSessionSaved?: (session: SavedSession) => void;
  onReportCreated?: (artifactId: string, title: string) => void;
}) {
  const [activeSessionId, setActiveSessionId] = useState<string | null>(initialSessionId ?? null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [history, setHistory] = useState<unknown[]>([]);
  const [input, setInput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [missingCreds, setMissingCreds] = useState<string[] | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/v1/google-ads/status`, { headers: authHeaders(false) })
      .then(r => r.json())
      .then((data: { configured: boolean; missing: string[] }) => {
        if (!data.configured) setMissingCreds(data.missing);
      })
      .catch(() => {});
  }, []);

  // Load session when initialSessionId changes (sidebar click).
  // IMPORTANT: skip reload if we're already on this session — the live state is the source of truth.
  useEffect(() => {
    if (!initialSessionId) {
      setActiveSessionId(null);
      setMessages([]);
      setHistory([]);
      return;
    }
    // If the component already has this session active, don't reload from localStorage.
    // This prevents onSessionSaved (called after run completes) from overwriting live messages.
    setActiveSessionId(prev => {
      if (prev === initialSessionId) return prev; // already on this session, no change
      // Switching to a different session — load from localStorage
      const all = loadSessions();
      const found = all.find(s => s.id === initialSessionId);
      if (found) {
        setMessages(found.messages);
        setHistory(found.history);
      } else {
        setMessages([]);
        setHistory([]);
      }
      return initialSessionId;
    });
  }, [initialSessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Persist session to localStorage and notify parent
  const persistSession = useCallback((msgs: ChatMessage[], hist: unknown[], id: string) => {
    if (msgs.length === 0) return;
    const title = sessionTitle(msgs);
    const all = loadSessions();
    const existing = all.findIndex(s => s.id === id);
    const updated: SavedSession = {
      id, title,
      createdAt: existing >= 0 ? all[existing].createdAt : new Date().toISOString(),
      messages: msgs,
      history: hist,
    };
    const next = existing >= 0
      ? all.map((s, i) => i === existing ? updated : s)
      : [updated, ...all];
    saveSessions(next);
    onSessionSaved?.(updated);
  }, [onSessionSaved]);

  const handleReset = () => {
    if (isRunning) return;
    setActiveSessionId(null);
    setMessages([]);
    setHistory([]);
    setInput('');
  };

  const handleSend = async (text?: string) => {
    const task = (text ?? input).trim();
    if (!task || isRunning) return;

    // Ensure we have a session id
    const sessionId = activeSessionId ?? `session_${Date.now()}`;
    if (!activeSessionId) setActiveSessionId(sessionId);

    setInput('');
    setIsRunning(true);

    // Register in RECENT sidebar immediately (lightweight — no localStorage write yet,
    // so the useEffect guard above prevents it from wiping live messages)
    if (!activeSessionId) {
      onSessionSaved?.({ id: sessionId, title: task.slice(0, 60), createdAt: new Date().toISOString(), messages: [], history: [] });
    }

    const userMsg: ChatMessage = { role: 'user', content: task };
    const agentMsg: ChatMessage = { role: 'agent', content: '', trace: [], status: 'streaming' };

    let currentMessages: ChatMessage[] = [];
    let agentIdx = -1;

    setMessages(prev => {
      currentMessages = [...prev, userMsg, agentMsg];
      agentIdx = currentMessages.length - 1;
      return currentMessages;
    });

    const updateAgent = (fn: (msg: Extract<ChatMessage, { role: 'agent' }>) => Partial<Extract<ChatMessage, { role: 'agent' }>>) => {
      setMessages(prev => {
        const next = prev.map((m, i) => {
          if (i !== agentIdx || m.role !== 'agent') return m;
          return { ...m, ...fn(m as Extract<ChatMessage, { role: 'agent' }>) };
        });
        currentMessages = next;
        return next;
      });
    };

    let finalHistory = history;

    try {
      await coworkApi.googleAdsAgent.run(task, (event: GoogleAdsAgentEvent) => {
        switch (event.type) {
          case 'step':
            updateAgent(m => ({ trace: [...m.trace, { kind: 'step', message: event.message }] }));
            break;
          case 'credentials_missing':
            setMissingCreds(event.missing);
            updateAgent(() => ({ status: 'error', error: 'Google Ads credentials not configured.' }));
            setIsRunning(false);
            break;
          case 'tool_call': {
            const id = `${event.name}_${Date.now()}`;
            updateAgent(m => ({ trace: [...m.trace, { kind: 'tool_call', name: event.name, input: event.input, id, status: 'running' as const }] }));
            break;
          }
          case 'tool_result':
            updateAgent(m => ({
              trace: m.trace.map(t => {
                if (t.kind !== 'tool_call' || t.status !== 'running' || t.name !== event.name) return t;
                return { ...t, id: event.toolUseId, result: event.preview, status: 'done' as const };
              }),
            }));
            break;
          case 'write_proposed':
            updateAgent(m => ({ trace: [...m.trace, { kind: 'write_proposed', action: event.action, details: event.details, toolName: event.toolName, toolInput: event.toolInput }] }));
            break;
          case 'report_created':
            updateAgent(() => ({ reportCreated: { artifactId: event.artifactId, title: event.title } }));
            onReportCreated?.(event.artifactId, event.title);
            break;
          case 'text_chunk':
            updateAgent(m => ({ content: m.content + event.content }));
            break;
          case 'done':
            updateAgent(() => ({ status: 'done' }));
            break;
          case 'history_update':
            finalHistory = event.history as unknown[];
            setHistory(finalHistory);
            break;
          case 'error':
            updateAgent(() => ({ status: 'error', error: event.message }));
            break;
        }
      }, history);
    } catch (err) {
      updateAgent(() => ({ status: 'error', error: err instanceof Error ? err.message : 'Agent failed' }));
    } finally {
      setIsRunning(false);
      textareaRef.current?.focus();
      // Save to localStorage after completion
      setMessages(latest => {
        persistSession(latest, finalHistory, sessionId);
        return latest;
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="px-8 pt-7 pb-5 border-b border-gray-100 shrink-0">
        <button onClick={onBack} className="flex items-center gap-1.5 text-[13px] text-[#667085] hover:text-[#344054] mb-4 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'linear-gradient(135deg, #4285f4, #34a853)' }}>
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-[18px] text-[#101828] font-bold leading-tight">Google Ads Agent</h1>
              <p className="text-[12px] text-[#667085]">Campaign audits · Keywords · Wasted spend · Budget optimization</p>
            </div>
          </div>
          {!isEmpty && (
            <button
              onClick={handleReset}
              disabled={isRunning}
              className="flex items-center gap-1.5 text-[12px] text-[#667085] hover:text-[#344054] px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-40"
            >
              <RotateCcw className="w-3.5 h-3.5" /> New chat
            </button>
          )}
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">

          {/* Empty / welcome state */}
          {isEmpty && !missingCreds && (
            <div className="space-y-6">
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 rounded-lg shrink-0 flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #4285f4, #34a853)' }}>
                  <TrendingUp className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-white border border-gray-100 rounded-xl px-4 py-3">
                  <p className="text-[13px] text-[#344054]">
                    Hi! I'm your Google Ads specialist. I have live access to your account and can audit campaigns,
                    find wasted spend, analyze keywords, and propose optimizations. What would you like to explore?
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 ml-10">
                {SUGGESTED.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(s)}
                    className="text-left px-3.5 py-3 bg-white border border-gray-100 rounded-xl text-[12px] text-[#344054] hover:border-[#4285f4]/30 hover:bg-blue-50/30 transition-all leading-relaxed"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {missingCreds && <SetupGuide missing={missingCreds} />}

          {messages.map((msg, i) => {
            if (msg.role === 'user') {
              return (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-3" style={{ background: '#4285f4' }}>
                    <p className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: '#ffffff' }}>{msg.content}</p>
                  </div>
                </div>
              );
            }
            return <AgentBubble key={i} msg={msg} onExecuteAction={(action) => handleSend(action)} />;
          })}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="px-8 pb-6 pt-3 border-t border-gray-100 shrink-0">
          <div className={`flex items-end gap-3 bg-white border rounded-2xl px-4 py-3 transition-colors ${isRunning ? 'border-gray-200' : 'border-gray-200 focus-within:border-[#4285f4]/40'}`}>
            <textarea
              ref={textareaRef}
              className="flex-1 text-[13px] text-[#101828] bg-transparent outline-none resize-none placeholder:text-[#c4c9d4] max-h-32"
              rows={1}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isRunning ? 'Agent is working…' : 'Ask anything about your Google Ads account…'}
              disabled={isRunning || !!missingCreds}
              style={{ lineHeight: '1.5' }}
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || isRunning || !!missingCreds}
              className="w-8 h-8 rounded-xl flex items-center justify-center transition-all disabled:opacity-30 shrink-0"
              style={{ background: 'linear-gradient(135deg, #4285f4, #34a853)' }}
            >
              {isRunning
                ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                : <Send className="w-3.5 h-3.5 text-white" />
              }
            </button>
          </div>
          <p className="text-[11px] text-[#c4c9d4] mt-2 text-center">
            Write operations (pause, budget changes) require your confirmation before applying.
          </p>
        </div>
      </div>
    </div>
  );
}
